import "@dxos/node-std/globals"

// packages/core/echo/echo-schema/src/defs.ts
var schema = Symbol.for("dxos.echo.schema");
var proxy = Symbol.for("dxos.echo.proxy");
var base = Symbol.for("dxos.echo.base");
var db = Symbol.for("dxos.echo.db");
var data = Symbol.for("dxos.echo.data");

// packages/core/echo/echo-schema/src/object.ts
import assert from "@dxos/node-std/assert";
import { createModelMutation, encodeModelMutation } from "@dxos/echo-db";
import { PublicKey } from "@dxos/keys";
var _a;
var EchoObject = class {
  constructor(modelConstructor) {
    /**
    * @internal
    */
    this._id = PublicKey.random().toHex();
    /** Proxied object. */
    this[_a] = this;
    this._modelConstructor = modelConstructor;
    this._id = PublicKey.random().toHex();
    this._stateMachine = this._modelConstructor.meta.stateMachine();
    this._model = new this._modelConstructor(this._modelConstructor.meta, this._id, () => this._getState(), async (mutation) => {
      this._mutate(mutation);
      return {
        feedKey: PublicKey.from("00"),
        seq: 0,
        waitToBeProcessed: () => Promise.resolve()
      };
    });
  }
  /** ID accessor. */
  get id() {
    return this[base]._id;
  }
  /** Database reference if bound. */
  get [(_a = base, db)]() {
    return this[base]._database;
  }
  /**
  * @internal
  * Called before object is bound to database.
  * `_database` is guaranteed to be set.
  */
  _beforeBind() {
  }
  /**
  * @internal
  * Called after object is bound to database.
  */
  _afterBind() {
  }
  /**
  * @internal
  * Called before object is bound to database.
  * `_database` is guaranteed to be set.
  */
  _itemUpdate() {
  }
  /**
  * @internal
  * Called when the object is imported to the database. Assigns the backing item.
  */
  _bind(item) {
    this._stateMachine = void 0;
    this._item = item;
    this._afterBind();
  }
  /**
  * Snapshot current state.
  * @internal
  */
  _createSnapshot() {
    if (this._stateMachine) {
      assert(this._modelConstructor.meta.snapshotCodec);
      return this._modelConstructor.meta.snapshotCodec.encodeAsAny(this._stateMachine.snapshot());
    } else {
      throw new Error("Only implemented on unpersisted objects.");
    }
  }
  /**
  * @internal
  */
  _getState() {
    if (this._stateMachine) {
      return this._stateMachine.getState();
    } else {
      assert(this._item);
      return this._item.state;
    }
  }
  /**
  * @returns Is this object currently persisted in the database.
  * @internal
  */
  _isPersisted() {
    return !!this._item;
  }
  /**
  * Perform mutation on this object's state.
  * Mutation is applied optimistically: calls to _getState() will return mutated state.
  * @returns Mutation result for the database or undefined if the current object is not persisted.
  * @internal
  */
  _mutate(mutation) {
    if (this._stateMachine) {
      this._stateMachine.process(mutation);
    } else {
      assert(this._database);
      return this._database._backend.mutate(createModelMutation(this._id, encodeModelMutation(this._model.modelMeta, mutation)));
    }
  }
};

// packages/core/echo/echo-schema/src/echo-array.ts
import assert2 from "@dxos/node-std/assert";
import { OrderedArray, Reference } from "@dxos/document-model";
import { log } from "@dxos/log";
var isIndex = (property) => typeof property === "string" && parseInt(property).toString() === property;
var _a2;
var EchoArray = class {
  constructor(items = []) {
    /**
    * Until this array is attached a document, the items are stored in this array.
    */
    this._uninitialized = [];
    this[_a2] = this;
    this._uninitialized = [
      ...items
    ];
    return new Proxy(this, {
      get: (target, property, receiver) => {
        if (isIndex(property)) {
          return this._get(+property);
        } else {
          return Reflect.get(target, property, receiver);
        }
      },
      set: (target, property, value, receiver) => {
        if (isIndex(property)) {
          this._set(+property, value);
          return true;
        } else {
          return Reflect.set(target, property, value, receiver);
        }
      }
    });
  }
  static get [Symbol.species]() {
    return Array;
  }
  [(_a2 = base, Symbol.iterator)]() {
    return this.values();
  }
  [Symbol.unscopables]() {
    throw new Error("Method not implemented.");
  }
  get length() {
    const model = this._getBackingModel();
    if (model) {
      const array = model.get(this._property);
      if (!array) {
        return 0;
      }
      assert2(array instanceof OrderedArray);
      return array.array.length;
    } else {
      assert2(this._uninitialized);
      return this._uninitialized.length;
    }
  }
  toString() {
    throw new Error("Method not implemented.");
  }
  toLocaleString() {
    throw new Error("Method not implemented.");
  }
  pop() {
    throw new Error("Method not implemented.");
  }
  concat(...items) {
    throw new Error("Method not implemented.");
  }
  join(separator) {
    throw new Error("Method not implemented.");
  }
  reverse() {
    throw new Error("Method not implemented.");
  }
  shift() {
    throw new Error("Method not implemented.");
  }
  slice(start, end) {
    return Array.from(this.values()).slice(start, end);
  }
  sort(compareFn) {
    throw new Error("Method not implemented.");
  }
  splice(start, deleteCount, ...items) {
    const model = this._getBackingModel();
    if (model) {
      const deletedItems = deleteCount !== void 0 ? this.slice(start, start + deleteCount) : [];
      void model.builder().arrayDelete(this._property, start, deleteCount).arrayInsert(this._property, start, items.map((item) => this._encode(item))).commit();
      return deletedItems;
    } else {
      assert2(this._uninitialized);
      return this._uninitialized.splice(start, deleteCount, ...items);
    }
  }
  unshift(...items) {
    throw new Error("Method not implemented.");
  }
  indexOf(searchElement, fromIndex) {
    return Array.from(this.values()).indexOf(searchElement, fromIndex);
  }
  lastIndexOf(searchElement, fromIndex) {
    return Array.from(this.values()).lastIndexOf(searchElement, fromIndex);
  }
  every(predicate, thisArg) {
    return Array.from(this.values()).every(predicate, thisArg);
  }
  some(predicate, thisArg) {
    return Array.from(this.values()).some(predicate, thisArg);
  }
  forEach(callbackfn, thisArg) {
    Array.from(this.values()).forEach(callbackfn, thisArg);
  }
  map(callbackfn, thisArg) {
    return Array.from(this.values()).map(callbackfn, thisArg);
  }
  filter(predicate, thisArg) {
    return Array.from(this.values()).filter(predicate, thisArg);
  }
  reduce(callbackfn, initialValue) {
    return Array.from(this.values()).reduce(callbackfn, initialValue);
  }
  reduceRight(callbackfn, initialValue) {
    return Array.from(this.values()).reduceRight(callbackfn, initialValue);
  }
  find(predicate, thisArg) {
    return Array.from(this.values()).find(predicate, thisArg);
  }
  findIndex(predicate, thisArg) {
    return Array.from(this.values()).findIndex(predicate, thisArg);
  }
  fill(value, start, end) {
    throw new Error("Method not implemented.");
  }
  copyWithin(target, start, end) {
    throw new Error("Method not implemented.");
  }
  entries() {
    throw new Error("Method not implemented.");
  }
  keys() {
    throw new Error("Method not implemented.");
  }
  values() {
    const model = this._getBackingModel();
    if (model) {
      const array = model.get(this._property);
      if (!array) {
        return [][Symbol.iterator]();
      }
      assert2(array instanceof OrderedArray);
      return array.toArray().map((value) => this._decode(value)).filter(Boolean).values();
    } else {
      assert2(this._uninitialized);
      return this._uninitialized[Symbol.iterator]();
    }
  }
  includes(searchElement, fromIndex) {
    return Array.from(this.values()).includes(searchElement, fromIndex);
  }
  flatMap(callback, thisArg) {
    return Array.from(this.values()).flatMap(callback, thisArg);
  }
  flat(depth) {
    throw new Error("Method not implemented.");
  }
  at(index) {
    const trueIndex = index < 0 ? this.length + index : index;
    return this._get(trueIndex);
  }
  push(...items) {
    const model = this._getBackingModel();
    if (model) {
      void model.builder().arrayPush(this._property, items.map((item) => this._encode(item))).commit();
    } else {
      assert2(this._uninitialized);
      this._uninitialized.push(...items);
    }
    return this.length;
  }
  //
  // Impl.
  //
  _getBackingModel() {
    var _a3;
    return (_a3 = this._object) == null ? void 0 : _a3._model;
  }
  _decode(value) {
    if (value instanceof Reference) {
      return this._object._lookupLink(value.itemId);
    } else if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      return decodeRecords(value, this._object);
    } else {
      return value;
    }
  }
  _encode(value) {
    if (value instanceof EchoObject) {
      void this._object._linkObject(value);
      return new Reference(value.id);
    } else if (typeof value === "object" && value !== null && Object.getOwnPropertyNames(value).length === 1 && value["@id"]) {
      return new Reference(value["@id"]);
    } else if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      log("Freezing object before encoding", value, {
        file: "echo-array.ts",
        line: 336,
        scope: this,
        callSite: (f, a) => f(...a)
      });
      Object.freeze(value);
      return encodeRecords(value, this._object);
    } else {
      assert2(value === null || value === void 0 || typeof value === "boolean" || typeof value === "number" || typeof value === "string", `Invalid type: ${JSON.stringify(value)}`);
      return value;
    }
  }
  /**
  * @internal
  */
  _attach(document, property) {
    this._object = document;
    this._property = property;
    this._uninitialized = void 0;
    return this;
  }
  _get(index) {
    if (this._getBackingModel()) {
      return this._getModel(index);
    } else {
      assert2(this._uninitialized);
      return this._uninitialized[index];
    }
  }
  _set(index, value) {
    if (this._getBackingModel()) {
      this._setModel(index, value);
    } else {
      assert2(this._uninitialized);
      this._uninitialized[index] = value;
    }
  }
  _getModel(index) {
    const model = this._getBackingModel();
    const array = model.get(this._property);
    assert2(array instanceof OrderedArray);
    return this._decode(array.get(index));
  }
  _setModel(index, value) {
    const model = this._getBackingModel();
    void model.builder().arrayDelete(this._property, index).arrayInsert(this._property, index, [
      this._encode(value)
    ]).commit();
  }
};
var encodeRecords = (value, document) => {
  if (value instanceof EchoObject) {
    void document._linkObject(value);
    return new Reference(value.id);
  } else if (Array.isArray(value)) {
    return value.map((value2) => encodeRecords(value2, document));
  } else if (typeof value === "object") {
    return Object.fromEntries(Object.entries(value).map(([key, value2]) => [
      key,
      encodeRecords(value2, document)
    ]));
  }
  return value;
};
var decodeRecords = (value, document) => {
  if (value instanceof Reference) {
    return document._lookupLink(value.itemId);
  } else if (Array.isArray(value)) {
    return value.map((value2) => decodeRecords(value2, document));
  } else if (typeof value === "object") {
    return Object.fromEntries(Object.entries(value).map(([key, value2]) => [
      key,
      decodeRecords(value2, document)
    ]));
  }
  return value;
};

// packages/core/echo/echo-schema/src/text-object.ts
import { TextModel } from "@dxos/text-model";
var Text = class extends EchoObject {
  // TODO(burdon): Change to object.
  constructor(text, kind, field) {
    var _a3;
    super(TextModel);
    const mutation = {};
    if (kind) {
      mutation.kind = kind;
    }
    if (field) {
      mutation.field = field;
    }
    if (Object.keys(mutation).length > 0) {
      this._mutate(mutation);
    }
    if (text) {
      (_a3 = this.model) == null ? void 0 : _a3.insert(text, 0);
    }
  }
  toString() {
    return this.text;
  }
  get kind() {
    var _a3;
    return (_a3 = this.model) == null ? void 0 : _a3.kind;
  }
  get doc() {
    var _a3, _b;
    (_a3 = this._database) == null ? void 0 : _a3._logObjectAccess(this);
    return (_b = this._model) == null ? void 0 : _b.doc;
  }
  get content() {
    var _a3;
    return (_a3 = this.model) == null ? void 0 : _a3.content;
  }
  get model() {
    var _a3;
    (_a3 = this._database) == null ? void 0 : _a3._logObjectAccess(this);
    return this._model;
  }
  /**
  * Returns the text content of the object.
  */
  get text() {
    var _a3;
    (_a3 = this._database) == null ? void 0 : _a3._logObjectAccess(this);
    return this._model.textContent;
  }
  toJSON() {
    return {
      "@id": this.id,
      "@model": TextModel.meta.type,
      text: this.text
    };
  }
  _afterBind() {
    this._model.initialize();
  }
  _itemUpdate() {
    this._model.initialize();
  }
};

// packages/core/echo/echo-schema/src/util.ts
var isReferenceLike = (value) => typeof value === "object" && value !== null && Object.getOwnPropertyNames(value).length === 1 && typeof value["@id"] === "string";

// packages/core/echo/echo-schema/src/typed-object.ts
import assert3 from "@dxos/node-std/assert";
import { inspect } from "@dxos/node-std/util";
import { DocumentModel, OrderedArray as OrderedArray2, Reference as Reference2 } from "@dxos/document-model";
import { TextModel as TextModel2 } from "@dxos/text-model";
var isValidKey = (key) => !(typeof key === "symbol" || key.startsWith("@@__") || key === "constructor" || key === "$$typeof" || key === "toString" || key === "toJSON" || key === "id" || key === "__deleted" || key === "__typename");
var isTypedObject = (object) => typeof object === "object" && object !== null && !!object[base];
var DEFAULT_VISITORS = {
  onRef: (id, obj) => ({
    "@id": id
  })
};
var TypedObjectImpl = class extends EchoObject {
  // TODO(burdon): Remove undefined keys.
  // prettier-ignore
  constructor(initialProps, _schemaType) {
    super(DocumentModel);
    this._schemaType = _schemaType;
    this._linkCache = /* @__PURE__ */ new Map();
    if (this._schemaType) {
      this._mutate({
        type: this._schemaType.name
      });
      for (const field of this._schemaType.fields) {
        if (field.type.kind === "array") {
          this._set(field.name, new EchoArray());
        } else if (field.type.kind === "ref" && field.type.modelType === TextModel2.meta.type) {
          this._set(field.name, new Text());
        }
      }
    }
    if (initialProps) {
      for (const field1 in initialProps) {
        const value = initialProps[field1];
        this._set(field1, value);
      }
    }
    return this._createProxy(this);
  }
  get [Symbol.toStringTag]() {
    var _a3, _b, _c;
    return (_c = (_b = (_a3 = this[base]) == null ? void 0 : _a3._schemaType) == null ? void 0 : _b.name) != null ? _c : "TypedObject";
  }
  /**
  * Fully qualified name of the object type for objects created from the schema.
  * @example "example.kai.Task"
  */
  get __typename() {
    var _a3, _b, _c, _d, _e;
    return (_e = (_d = (_b = (_a3 = this[base]) == null ? void 0 : _a3._schemaType) == null ? void 0 : _b.name) != null ? _d : (_c = this[base]._model) == null ? void 0 : _c.type) != null ? _e : void 0;
  }
  /**
  * Returns the schema type descriptor for the object.
  */
  // TODO(burdon): Method on TypedObject vs EchoObject?
  get [schema]() {
    var _a3;
    return (_a3 = this[base]) == null ? void 0 : _a3._schemaType;
  }
  /** Deletion. */
  get __deleted() {
    var _a3, _b;
    return (_b = (_a3 = this[base]._item) == null ? void 0 : _a3.deleted) != null ? _b : false;
  }
  /**
  * Convert to JSON object. Used by `JSON.stringify`.
  * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/JSON/stringify#description
  */
  toJSON() {
    return this[base]._convert();
  }
  get [data]() {
    return {
      "@id": this.id,
      "@type": this.__typename,
      "@model": DocumentModel.meta.type,
      ...this[base]._convert({
        onRef: (id, obj) => obj != null ? obj : {
          "@id": id
        }
      })
    };
  }
  /**
  * @internal
  */
  _convert(visitors = {}) {
    var _a3;
    const visitorsWithDefaults = {
      ...DEFAULT_VISITORS,
      ...visitors
    };
    const convert = (value) => {
      if (value instanceof EchoObject) {
        return visitorsWithDefaults.onRef(value.id, value);
      } else if (value instanceof Reference2) {
        return visitorsWithDefaults.onRef(value.itemId, this._lookupLink(value.itemId));
      } else if (value instanceof OrderedArray2) {
        return value.toArray().map(convert);
      } else if (value instanceof EchoArray) {
        return value.map(convert);
      } else if (Array.isArray(value)) {
        return value.map(convert);
      } else if (typeof value === "object" && value !== null) {
        const result = {};
        for (const key of Object.keys(value)) {
          result[key] = convert(value[key]);
        }
        return result;
      } else {
        return value;
      }
    };
    return {
      "@id": this.id,
      "@type": this.__typename,
      "@model": DocumentModel.meta.type,
      ...convert((_a3 = this._model) == null ? void 0 : _a3.toObject())
    };
  }
  [inspect.custom](depth, options, inspect_) {
    return `${this[Symbol.toStringTag]} ${inspect(this[data])}`;
  }
  // get [devtoolsFormatter](): DevtoolsFormatter {
  //   return {
  //     header: () => [
  //       'span',
  //       {},
  //       ['span', {}, `${this[Symbol.toStringTag]}(`],
  //       ['span', {}, this.id],
  //       ['span', {}, ')']
  //     ],
  //     hasBody: () => true,
  //     body: () => {
  //       const json = this.toJSON();
  //       return null
  //     }
  //   };
  // }
  /**
  * @internal
  */
  _get(key) {
    var _a3;
    (_a3 = this._database) == null ? void 0 : _a3._logObjectAccess(this);
    let type;
    const value = this._model.get(key);
    if (!type && this._schemaType) {
      const field = this._schemaType.fields.find((field2) => field2.name === key);
      if ((field == null ? void 0 : field.type.kind) === "array") {
        type = "array";
      }
    }
    if (!type && value instanceof OrderedArray2) {
      type = "array";
    }
    if (!type && value instanceof Reference2) {
      type = "ref";
    }
    if (!type && typeof value === "object" && value !== null) {
      type = "object";
    }
    switch (type) {
      case "ref":
        return this._lookupLink(value.itemId);
      case "object":
        return this._createProxy({}, key);
      case "array":
        return new EchoArray()._attach(this[base], key);
      default:
        return value;
    }
  }
  /**
  * @internal
  */
  _set(key, value) {
    var _a3;
    (_a3 = this._database) == null ? void 0 : _a3._logObjectAccess(this);
    this._inBatch(() => {
      if (value instanceof EchoObject) {
        this._linkObject(value);
        this._mutate(this._model.builder().set(key, new Reference2(value.id)).build());
      } else if (value instanceof EchoArray) {
        const values = value.map((item) => {
          if (item instanceof EchoObject) {
            this._linkObject(item);
            return new Reference2(item.id);
          } else if (isReferenceLike(item)) {
            return new Reference2(item["@id"]);
          } else {
            return item;
          }
        });
        this._mutate(this._model.builder().set(key, OrderedArray2.fromValues(values)).build());
        value._attach(this[base], key);
      } else if (Array.isArray(value)) {
        this._mutate(this._model.builder().set(key, OrderedArray2.fromValues([])).build());
        this._get(key).push(...value);
      } else if (typeof value === "object" && value !== null) {
        if (Object.getOwnPropertyNames(value).length === 1 && value["@id"]) {
          this._mutate(this._model.builder().set(key, new Reference2(value["@id"])).build());
        } else {
          const sub = this._createProxy({}, key);
          for (const [subKey, subValue] of Object.entries(value)) {
            sub[subKey] = subValue;
          }
        }
      } else {
        this._mutate(this._model.builder().set(key, value).build());
      }
    });
  }
  _inBatch(cb) {
    var _a3;
    if (!((_a3 = this._database) == null ? void 0 : _a3._backend)) {
      cb();
    } else {
      const batchCreated = this._database._backend.beginBatch();
      try {
        cb();
      } finally {
        if (batchCreated) {
          this._database._backend.commitBatch();
        }
      }
    }
  }
  /**
  * Create proxy for root or sub-object.
  * @internal
  */
  _createProxy(object, parent) {
    const getProperty = (property) => parent ? `${parent}.${property}` : property;
    return new Proxy(object, {
      ownKeys(target) {
        var _a3, _b;
        return (_b = (_a3 = target._schemaType) == null ? void 0 : _a3.fields.map(({ name }) => name)) != null ? _b : [];
      },
      /**
      * Called for each property (e.g., called by Object.keys()).
      * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Proxy/Proxy/getOwnPropertyDescriptor
      * See: https://javascript.info/proxy
      */
      getOwnPropertyDescriptor(target, property) {
        return {
          enumerable: true,
          configurable: true
        };
      },
      get: (target, property, receiver) => {
        if (property === proxy) {
          return true;
        }
        if (!isValidKey(property)) {
          switch (property) {
            case "toJSON": {
              return this.toJSON.bind(this);
            }
            default: {
              return Reflect.get(this, property, receiver);
            }
          }
        }
        return this._get(getProperty(property));
      },
      set: (target, property, value, receiver) => {
        if (!isValidKey(property)) {
          return Reflect.set(this, property, value, receiver);
        }
        this._set(getProperty(property), value);
        return true;
      }
    });
  }
  _beforeBind() {
    assert3(this._linkCache);
    for (const obj of this._linkCache.values()) {
      this._database.add(obj);
    }
    this._linkCache = void 0;
  }
  /**
  * Store referenced object.
  * @internal
  */
  _linkObject(obj) {
    if (this._database) {
      this._database.add(obj);
    } else {
      assert3(this._linkCache);
      this._linkCache.set(obj.id, obj);
    }
  }
  /**
  * Lookup referenced object.
  * @internal
  */
  _lookupLink(id) {
    if (this._database) {
      return this._database.getObjectById(id);
    } else {
      assert3(this._linkCache);
      return this._linkCache.get(id);
    }
  }
};
Object.defineProperty(TypedObjectImpl, "name", {
  value: "TypedObject"
});
var TypedObject = TypedObjectImpl;
var Expando = TypedObject;

// packages/core/echo/echo-schema/src/query.ts
var Query = class {
  constructor(_dbObjects, _updateEvent, _filter) {
    this._dbObjects = _dbObjects;
    this._updateEvent = _updateEvent;
    this._filter = _filter;
  }
  get objects() {
    if (!this._cache) {
      this._cache = Array.from(this._dbObjects.values()).filter((obj) => filterMatcher(this._filter, obj));
    }
    return this._cache;
  }
  subscribe(callback) {
    return this._updateEvent.on((updated) => {
      const changed = updated.some((object) => {
        var _a3;
        if (this._dbObjects.has(object.id)) {
          const match = filterMatcher(this._filter, this._dbObjects.get(object.id));
          const exists = (_a3 = this._cache) == null ? void 0 : _a3.find((obj) => obj.id === object.id);
          return match || exists && !match;
        } else {
          return false;
        }
      });
      if (changed) {
        this._cache = void 0;
        callback(this);
      }
    });
  }
};
var filterMatcher = (filter, object) => {
  if (!isTypedObject(object)) {
    return false;
  }
  if (object.__deleted) {
    return false;
  }
  if (typeof filter === "object" && filter["@type"] && object.__typename !== filter["@type"]) {
    return false;
  }
  if (typeof filter === "function") {
    return filter(object);
  } else if (typeof filter === "object" && filter !== null) {
    for (const key in filter) {
      if (key === "@type") {
        continue;
      }
      if (object[key] !== filter[key]) {
        return false;
      }
    }
  }
  return true;
};

// packages/core/echo/echo-schema/src/database.ts
import assert4 from "@dxos/node-std/assert";
import { Event } from "@dxos/async";
import { DocumentModel as DocumentModel2 } from "@dxos/document-model";
import { log as log2 } from "@dxos/log";
import { EchoObject as EchoObjectProto } from "@dxos/protocols/proto/dxos/echo/object";
import { TextModel as TextModel3 } from "@dxos/text-model";
var EchoDatabase = class {
  constructor(_itemManager, _backend, _router) {
    this._itemManager = _itemManager;
    this._backend = _backend;
    this._router = _router;
    this._objects = /* @__PURE__ */ new Map();
    this._updateEvent = new Event();
    this._backend.itemUpdate.on(this._update.bind(this));
    this._update([]);
  }
  get objects() {
    return Array.from(this._objects.values());
  }
  get router() {
    return this._router;
  }
  // TODO(burdon): Return type via generic?
  getObjectById(id) {
    const obj = this._objects.get(id);
    if (!obj) {
      return void 0;
    }
    if (obj.__deleted === true) {
      return void 0;
    }
    return obj;
  }
  /**
  * Add object to th database.
  * Restores the object if it was deleted.
  */
  add(obj) {
    log2("save", {
      id: obj.id,
      type: obj.__typename
    }, {
      file: "database.ts",
      line: 70,
      scope: this,
      callSite: (f, a) => f(...a)
    });
    assert4(obj.id);
    assert4(obj[base]);
    if (obj[base]._database) {
      this._backend.mutate({
        objects: [
          {
            objectId: obj[base]._id,
            mutations: [
              {
                action: EchoObjectProto.Mutation.Action.RESTORE
              }
            ]
          }
        ]
      });
      return obj;
    }
    assert4(!obj[db]);
    obj[base]._database = this;
    this._objects.set(obj[base]._id, obj);
    const batchCreated = this._backend.beginBatch();
    try {
      obj[base]._beforeBind();
      const snapshot = obj[base]._createSnapshot();
      const result = this._backend.mutate({
        objects: [
          {
            objectId: obj[base]._id,
            genesis: {
              modelType: obj[base]._modelConstructor.meta.type
            },
            snapshot: {
              // TODO(dmaretskyi): Parent id, deleted flag.
              model: snapshot
            }
          }
        ]
      });
      assert4(result.objectsUpdated.length === 1);
      obj[base]._bind(result.objectsUpdated[0]);
    } finally {
      if (batchCreated) {
        this._backend.commitBatch();
      }
    }
    return obj;
  }
  /**
  * Remove object.
  */
  remove(obj) {
    this._backend.mutate({
      objects: [
        {
          objectId: obj[base]._id,
          mutations: [
            {
              action: EchoObjectProto.Mutation.Action.DELETE
            }
          ]
        }
      ]
    });
  }
  /**
  * Wait for all pending operations to complete.
  */
  async flush() {
    await this._backend.flush();
  }
  query(filter) {
    return new Query(this._objects, this._updateEvent, filter);
  }
  /**
  * @internal
  */
  _logObjectAccess(obj) {
    this._router._logObjectAccess(obj);
  }
  _update(changed) {
    for (const object of this._itemManager.entities.values()) {
      if (!this._objects.has(object.id)) {
        const obj = this._createObjectInstance(object);
        if (!obj) {
          continue;
        }
        obj[base]._id = object.id;
        this._objects.set(object.id, obj);
        obj[base]._database = this;
        obj[base]._bind(object);
      }
    }
    for (const item of changed) {
      const obj1 = this._objects.get(item.id);
      if (obj1) {
        obj1[base]._itemUpdate();
      }
    }
    this._updateEvent.emit(changed);
  }
  /**
  * Create object with a proper prototype representing the given item.
  */
  _createObjectInstance(item) {
    var _a3;
    if (item.modelType === DocumentModel2.meta.type) {
      const type = item.state["@type"];
      if (!type) {
        return new TypedObject();
      }
      const Proto = (_a3 = this._router.schema) == null ? void 0 : _a3.getPrototype(type);
      if (!Proto) {
        log2.warn("Unknown schema type", {
          type
        }, {
          file: "database.ts",
          line: 205,
          scope: this,
          callSite: (f, a) => f(...a)
        });
        return new TypedObject();
      } else {
        return new Proto();
      }
    } else if (item.modelType === TextModel3.meta.type) {
      return new Text();
    } else {
      log2.warn("Unknown model type", {
        type: item.modelType
      }, {
        file: "database.ts",
        line: 213,
        scope: this,
        callSite: (f, a) => f(...a)
      });
      return void 0;
    }
  }
};

// packages/core/echo/echo-schema/src/schema.ts
import assert5 from "@dxos/node-std/assert";
import * as pb from "protobufjs";
import { DocumentModel as DocumentModel3 } from "@dxos/document-model";
import { TextModel as TextModel4 } from "@dxos/text-model";
import { stripKeys } from "@dxos/util";
var EchoSchemaType = class {
  // prettier-ignore
  constructor(_type) {
    this._type = _type;
    this.fields = [];
    this.fields = getFields(_type);
  }
  get name() {
    return this._type.fullName.slice(1);
  }
  get shortName() {
    return this._type.name;
  }
  createFilter(opts) {
    return {
      ...stripKeys(opts),
      "@type": this.name
    };
  }
};
var EchoSchema = class {
  static fromJson(json) {
    return new EchoSchema(pb.Root.fromJSON(JSON.parse(json)));
  }
  // prettier-ignore
  constructor(_root) {
    this._root = _root;
    this._prototypes = /* @__PURE__ */ new Map();
  }
  get types() {
    return Array.from(this._prototypes.keys()).map((name) => this.getType(name));
  }
  getType(name) {
    return new EchoSchemaType(this._root.lookupType(name));
  }
  mergeSchema(schema2) {
    const rootToMerge = filterNamespaces({
      base: this._root,
      toFilter: schema2._root
    });
    rootToMerge.nestedArray.forEach((nested) => {
      this._root.add(nested);
    });
    Array.from(schema2._prototypes.entries()).forEach(([name, prototype]) => {
      assert5(!this._prototypes.has(name), "Names collision");
      this._prototypes.set(name, prototype);
    });
  }
  /**
  * Called from generated code.
  */
  registerPrototype(proto) {
    this._prototypes.set(proto.type.name, proto);
  }
  getPrototype(name) {
    return this._prototypes.get(name);
  }
};
var filterNamespaces = ({ base: base2, toFilter, namespacesToRemove = [
  ".dxos.schema"
] }) => {
  const filtered = pb.Root.fromJSON(toFilter.toJSON());
  namespacesToRemove.forEach((namespace) => {
    const isNamespacePresent = !!base2.lookup(namespace);
    const namespaceToRemove = filtered.lookup(namespace);
    if (isNamespacePresent && namespaceToRemove) {
      const parentNamespace = namespace.split(".").slice(0, -1).join(".");
      const parent = filtered.lookup(parentNamespace);
      parent == null ? void 0 : parent.remove(namespaceToRemove);
    }
  });
  return filtered;
};
var getFields = (type) => {
  type.fieldsArray.forEach((field) => field.resolve());
  return type.fieldsArray.map((field) => {
    const echoField = {
      name: field.name,
      type: getEchoType(field)
    };
    if (field.options) {
      echoField.options = field.options;
    }
    return echoField;
  });
};
var getEchoType = (field) => {
  if (field.resolvedType) {
    if (field.repeated) {
      return {
        kind: "array",
        elementType: getComplexType(field.resolvedType)
      };
    } else {
      return getComplexType(field.resolvedType);
    }
  }
  if (field.repeated) {
    return {
      kind: "array",
      elementType: getBasicType(field.type)
    };
  } else {
    return getBasicType(field.type);
  }
};
var getComplexType = (type) => {
  if (type instanceof pb.Enum) {
    return {
      kind: "enum",
      enumType: type.fullName.slice(1)
    };
  } else if (type instanceof pb.Type) {
    if (isTextObject(type.fullName)) {
      return {
        kind: "ref",
        objectType: type.fullName.slice(1),
        modelType: TextModel4.meta.type
      };
    } else if (type.options && type.options["(object)"]) {
      return {
        kind: "ref",
        objectType: type.fullName.slice(1),
        modelType: DocumentModel3.meta.type
      };
    } else {
      return {
        kind: "record",
        objectType: type.fullName.slice(1)
      };
    }
  }
  throw new Error(`Unknown type: ${type}`);
};
var getBasicType = (type) => {
  switch (type) {
    case "double":
    case "float":
    case "int32":
    case "uint32":
    case "sint32":
    case "fixed32":
    case "sfixed32":
    case "int64":
    case "uint64":
    case "sint64":
    case "fixed64":
    case "sfixed64":
      return {
        kind: "number"
      };
    case "string":
      return {
        kind: "string"
      };
    case "bytes":
      return {
        kind: "bytes"
      };
    case "bool":
      return {
        kind: "boolean"
      };
    default:
      throw new Error(`Unknown type: ${type}`);
  }
};
var isTextObject = (typeName) => typeName.split(".").at(-1) === "Text";

// packages/core/echo/echo-schema/src/database-router.ts
import { Event as Event2 } from "@dxos/async";
import { PublicKey as PublicKey2 } from "@dxos/keys";
import { ComplexMap } from "@dxos/util";

// packages/core/echo/echo-schema/src/react-integration.ts
var getCurrentReactComponent = () => {
  var _a3, _b;
  try {
    if (typeof __REACT_DEVTOOLS_GLOBAL_HOOK__ !== "undefined") {
      const fiber = __REACT_DEVTOOLS_GLOBAL_HOOK__.renderers.get(1).getCurrentFiber();
      if (!fiber) {
        return void 0;
      }
      const { fileName, lineNumber, columnNumber } = (_b = (_a3 = fiber.child) == null ? void 0 : _a3._debugSource) != null ? _b : {};
      return {
        fileName: typeof fileName === "string" ? fileName : void 0,
        lineNumber: typeof lineNumber === "number" ? lineNumber : void 0,
        columnNumber: typeof columnNumber === "number" ? columnNumber : void 0
      };
    }
  } catch (e) {
    return void 0;
  }
};

// packages/core/echo/echo-schema/src/database-router.ts
var DatabaseRouter = class {
  constructor() {
    this._accessObserverStack = [];
    this._databases = new ComplexMap(PublicKey2.hash);
    this._update = new Event2();
    this._schema = EchoSchema.fromJson("{}");
  }
  get schema() {
    return this._schema;
  }
  addSchema(schema2) {
    this._schema.mergeSchema(schema2);
  }
  register(spaceKey, database) {
    this._databases.set(spaceKey, database);
    database._updateEvent.on((entities) => this._update.emit({
      spaceKey,
      changedEntities: entities
    }));
  }
  /**
  * Subscribe to database updates.
  * Calls the callback when any object from the selection changes.
  * Calls the callback when the selection changes.
  * Always calls the callback on the first `selection.update` call.
  */
  // TODO(burdon): Add filter?
  // TODO(burdon): Immediately trigger callback.
  createSubscription(onUpdate) {
    let subscribed = true;
    const unsubscribe = this._update.on(({ changedEntities }) => {
      if (changedEntities.some((entity) => handle.selectedIds.has(entity.id))) {
        onUpdate({});
      }
    });
    let firstUpdate = true;
    const handle = {
      update: (selection) => {
        const newIds = new Set(getIdsFromSelection(selection));
        const changed = !areSetsEqual(newIds, handle.selectedIds);
        handle.selectedIds = newIds;
        if (changed || firstUpdate) {
          firstUpdate = false;
          onUpdate({});
        }
        return handle;
      },
      subscribed,
      selectedIds: /* @__PURE__ */ new Set(),
      unsubscribe: () => {
        unsubscribe();
        subscribed = false;
      }
    };
    return handle;
  }
  createAccessObserver() {
    const observer = new AccessObserver(() => {
      this._accessObserverStack.splice(this._accessObserverStack.indexOf(observer), 1);
    });
    this._accessObserverStack.push(observer);
    return observer;
  }
  /**
  * @internal
  */
  _logObjectAccess(obj) {
    var _a3;
    (_a3 = this._accessObserverStack.at(-1)) == null ? void 0 : _a3.accessed.add(obj);
    if (this._accessObserverStack.length === 0) {
      const currentComponent = getCurrentReactComponent();
      if (currentComponent) {
      }
    }
  }
};
var AccessObserver = class {
  constructor(pop) {
    this.pop = pop;
    this.accessed = /* @__PURE__ */ new Set();
  }
};
var getIdsFromSelection = (selection) => {
  if (selection instanceof EchoObject) {
    return [
      selection[base]._id
    ];
  } else if (typeof selection === "function") {
    return [];
  } else if (!selection) {
    return [];
  } else {
    return selection.flatMap(getIdsFromSelection);
  }
};
var areSetsEqual = (a, b) => {
  if (a.size !== b.size) {
    return false;
  }
  for (const item of a) {
    if (!b.has(item)) {
      return false;
    }
  }
  return true;
};

export {
  schema,
  proxy,
  base,
  db,
  data,
  EchoObject,
  EchoArray,
  Text,
  isReferenceLike,
  isTypedObject,
  DEFAULT_VISITORS,
  TypedObject,
  Expando,
  Query,
  EchoDatabase,
  EchoSchemaType,
  EchoSchema,
  DatabaseRouter,
  AccessObserver
};
//# sourceMappingURL=chunk-RX3LKBXO.mjs.map
